import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Loader2, X } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { useAvailableClausesService } from "../services/use-available-clauses-service";
import { useAcceptClauseService } from "../services/use-accept-clause-service";
import type { ClauseResource } from "../types";

interface ClausesAcknowledgmentDialogProps {
  contractId?: string;
  /** Opened by the action the gate protects, not by the flow step itself. */
  open: boolean;
  /**
   * Every applicable clause has been accepted server-side. The caller resumes
   * the action it was holding back — the contract signature request.
   */
  onAllAccepted: () => void;
  /**
   * The reader backed out without accepting everything: nothing is recorded and
   * the signature stays held. The caller returns them to the contract, where
   * asking to sign again reopens the gate on what is still pending.
   */
  onDismiss?: () => void;
}

const formatAcceptedAt = (value: string, locale: string): string => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString(locale, {
    dateStyle: "long",
    timeStyle: "short",
  });
};

interface ClausePanelProps {
  clause: ClauseResource;
  checked: boolean;
  /** Acceptance timestamp, present only once the clause has been accepted. */
  acceptedAt: string | null;
  onCheckedChange: (checked: boolean) => void;
}

const ClausePanel: FC<ClausePanelProps> = ({
  clause,
  checked,
  acceptedAt,
  onCheckedChange,
}) => {
  const { t, i18n } = useTranslation(["clauses"]);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="w-full overflow-hidden rounded-xl border border-input bg-card shadow-xs">
        {/* No header here: the clause name and its accepted state are already
            carried by the list on the left. */}
        <div
          className="h-[46vh] overflow-y-auto px-5 py-4 text-sm leading-6 text-foreground"
          dangerouslySetInnerHTML={{
            __html: clause.attributes.content ?? "",
          }}
        />
      </div>

      {acceptedAt ? (
        <p className="rounded-xl border border-green-600/30 bg-green-600/5 px-4 py-3 text-sm text-foreground">
          {t(
            "clauses:accepted-at",
            "You accepted this clause on {{date}}.",
            { date: formatAcceptedAt(acceptedAt, i18n.language) }
          )}
        </p>
      ) : (
        <Label
          htmlFor={`clause-accept-${clause.id}`}
          className={cn(
            "flex cursor-pointer items-start gap-3 rounded-xl border bg-card p-4 leading-5 transition-colors",
            checked ? "border-primary/40 bg-primary/[0.03]" : "border-input"
          )}
        >
          <Checkbox
            id={`clause-accept-${clause.id}`}
            className="mt-0.5"
            checked={checked}
            onCheckedChange={(value) => onCheckedChange(!!value)}
          />
          <span className="block">
            {t(
              "clauses:acceptance-label",
              "I have read, understood and I accept the clause “{{name}}”.",
              { name: clause.attributes.name }
            )}
            <span className="text-destructive ml-1">*</span>
          </span>
        </Label>
      )}
    </div>
  );
};

/**
 * Mandatory clause acknowledgment gate: a modal opened when the client asks to
 * sign the contract. Each clause must be explicitly accepted
 * (checkbox + "I agree", recorded server-side with its own timestamp) before
 * `onAllAccepted` lets the signature request through — so every clause
 * acceptance provably precedes the contract being signed. Accepting keeps the
 * reader on the same clause, now stamped with its acceptance time; moving on is
 * an explicit step, and the gate only lifts once nothing is left to accept.
 *
 * Backing out is possible and records nothing: the signature is simply never
 * sent. Only deliberate exits close it (the header button, Escape) — a tap
 * outside is ignored, because on a phone the overlay is most of the screen and
 * losing a half-read clause to a stray touch is not a decision anyone made.
 *
 * Layout: the modal is bounded by the viewport and scrolls AS A WHOLE — the
 * panes inside keep their sizes and the reader scrolls the window to reach
 * them. See FD-7581: it used to be unbounded and `overflow-hidden`, so on a
 * phone it was clipped off both edges with the "I agree" button out of reach
 * and no way to scroll or close, and no signature was ever sent. Giving the
 * frame a fixed header and footer instead would be worse on a short screen —
 * at 286px tall there is nothing left for the clause text to occupy.
 */
const ClausesAcknowledgmentDialog: FC<ClausesAcknowledgmentDialogProps> = ({
  contractId,
  open,
  onAllAccepted,
  onDismiss,
}) => {
  const { t } = useTranslation(["clauses"]);

  const { data: clauses } = useAvailableClausesService(contractId);
  const acceptClause = useAcceptClauseService();

  // Snapshot of every applicable clause taken when the gate first engages:
  // keeps the list stable while acceptances are recorded server-side. Clauses
  // already accepted stay in the list — they are shown with their timestamp,
  // not hidden — so the reader always sees the full set.
  const [snapshot, setSnapshot] = useState<ClauseResource[] | null>(null);
  // clause id -> acceptance timestamp, seeded from what the server already
  // recorded and extended as clauses are accepted in this session.
  const [acceptedAtById, setAcceptedAtById] = useState<Record<string, string>>(
    {}
  );
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [currentClauseId, setCurrentClauseId] = useState<string | null>(null);
  const [hasAcceptError, setHasAcceptError] = useState(false);

  useEffect(() => {
    if (!open || !clauses || snapshot !== null) return;

    setSnapshot(clauses);
    setAcceptedAtById(
      Object.fromEntries(
        clauses
          .filter((clause) => clause.attributes.accepted_at)
          .map((clause) => [clause.id, clause.attributes.accepted_at as string])
      )
    );
    // Open on the first clause that still needs a decision.
    setCurrentClauseId(
      (clauses.find((clause) => !clause.attributes.accepted_at) ?? clauses[0])
        ?.id ?? null
    );
  }, [open, clauses, snapshot]);

  // Closing drops the snapshot so a later reopen re-reads what is still
  // pending instead of replaying stale clauses.
  useEffect(() => {
    if (open) return;

    setSnapshot(null);
    setAcceptedAtById({});
    setCheckedIds([]);
    setCurrentClauseId(null);
    setHasAcceptError(false);
  }, [open]);

  const allClauses = snapshot ?? [];

  const remainingClauses = useMemo(
    () => allClauses.filter((clause) => !acceptedAtById[clause.id]),
    [snapshot, acceptedAtById]
  );

  const hasSingleClause = allClauses.length === 1;
  const currentClause =
    allClauses.find((clause) => clause.id === currentClauseId) ?? null;
  const acceptedCount = allClauses.length - remainingClauses.length;
  const currentAcceptedAt = currentClause
    ? acceptedAtById[currentClause.id] ?? null
    : null;
  const isCurrentChecked =
    !!currentClause &&
    (!!currentAcceptedAt || checkedIds.includes(currentClause.id));

  const toggleChecked = (clauseId: string, checked: boolean) => {
    setCheckedIds((ids) =>
      checked
        ? ids.includes(clauseId)
          ? ids
          : [...ids, clauseId]
        : ids.filter((id) => id !== clauseId)
    );
  };

  const goToNextRemaining = (afterId: string, accepted: Record<string, string>) => {
    const afterIndex = allClauses.findIndex((clause) => clause.id === afterId);
    const next = [
      ...allClauses.slice(afterIndex + 1),
      ...allClauses.slice(0, afterIndex),
    ].find((clause) => !accepted[clause.id]);

    if (next) setCurrentClauseId(next.id);
  };

  const handleAgree = () => {
    if (!currentClause || !contractId) return;

    setHasAcceptError(false);
    acceptClause.mutate(
      { contractId, clauseId: currentClause.id },
      {
        onSuccess: (response) => {
          // Prefer the timestamp the backend recorded; the local clock is only
          // a display fallback when the response carries no attributes.
          const recordedAt =
            (response?.data as any)?.data?.attributes?.accepted_at ??
            new Date().toISOString();

          const accepted = { ...acceptedAtById, [currentClause.id]: recordedAt };
          setAcceptedAtById(accepted);

          // No auto-advance: the reader stays on the clause they just accepted
          // and sees its timestamp. The gate lifts only when none are left.
          if (!allClauses.some((clause) => !accepted[clause.id])) {
            onAllAccepted();
          }
        },
        onError: () => setHasAcceptError(true),
      }
    );
  };

  if (!open || !currentClause || remainingClauses.length === 0) return <></>;

  return (
    <Dialog open onOpenChange={(next) => !next && onDismiss?.()}>
      <DialogContent
        // Bounded by the viewport and scrollable as a whole: whatever the panes
        // inside add up to, the reader can always scroll the window down to the
        // acceptance checkbox and the "I agree" button. `100dvh` rather than
        // `100vh` because mobile Safari resolves `vh` against the viewport with
        // the URL bar hidden, which is taller than what the reader sees.
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-xl border-input bg-card p-0 gap-0 sm:max-w-4xl"
        showCloseButton={false}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="border-b px-6 py-5 pr-14 sm:pr-16">
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">
            {t("clauses:mandatory-approval", "Mandatory approval")}
          </p>
          <DialogTitle className="follo-label">
            {t("clauses:title", "Clauses to approve before concluding")}
          </DialogTitle>
          <DialogDescription>
            {hasSingleClause
              ? t(
                  "clauses:single-clause-description",
                  "Please read the clause below and accept it to continue."
                )
              : t(
                  "clauses:accepted-count",
                  "{{accepted}} of {{total}} clauses accepted",
                  { accepted: acceptedCount, total: allClauses.length }
                )}
          </DialogDescription>
        </DialogHeader>

        <DialogClose className="absolute top-3 right-3 flex size-10 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden">
          <X size={18} />
          <span className="sr-only">{t("clauses:close", "Close")}</span>
        </DialogClose>

        <div
          className={cn(
            "grid gap-5 px-6 py-5",
            !hasSingleClause && "md:grid-cols-[minmax(180px,15rem)_1fr]"
          )}
        >
          {!hasSingleClause && (
            <nav className="flex max-h-[54vh] flex-col gap-1 overflow-y-auto rounded-xl border border-input bg-muted/30 p-2">
              {allClauses.map((clause) => {
                const isAccepted = !!acceptedAtById[clause.id];
                const isCurrent = clause.id === currentClause.id;

                return (
                  <button
                    key={clause.id}
                    type="button"
                    onClick={() => setCurrentClauseId(clause.id)}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors",
                      isCurrent
                        ? "bg-card font-medium text-primary shadow-xs"
                        : "text-muted-foreground hover:bg-card/60"
                    )}
                  >
                    <span
                      className={cn(
                        "flex size-4 shrink-0 items-center justify-center rounded-full",
                        isAccepted
                          ? "bg-green-600 text-white"
                          : "border border-muted-foreground/40"
                      )}
                    >
                      {isAccepted && <Check size={11} strokeWidth={3} />}
                    </span>
                    <span className="truncate">{clause.attributes.name}</span>
                  </button>
                );
              })}
            </nav>
          )}

          <div className="flex min-w-0 flex-col gap-4">
            <ClausePanel
              clause={currentClause}
              checked={isCurrentChecked}
              acceptedAt={currentAcceptedAt}
              onCheckedChange={(value) =>
                toggleChecked(currentClause.id, value)
              }
            />

            {hasAcceptError && (
              <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {t(
                  "clauses:accept-error",
                  "We could not save your acceptance. Please try again."
                )}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t bg-card px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            {t(
              "clauses:signature-blocked-notice",
              "The contract can only be signed once every clause is accepted"
            )}
          </p>
          {currentAcceptedAt ? (
            <Button
              className="cursor-pointer"
              variant="outline"
              onClick={() =>
                goToNextRemaining(currentClause.id, acceptedAtById)
              }
            >
              {t("clauses:next-clause", "Next clause")}
              <ArrowRight size={15} />
            </Button>
          ) : (
            <Button
              className="cursor-pointer"
              disabled={!isCurrentChecked || acceptClause.isPending}
              onClick={handleAgree}
            >
              {acceptClause.isPending ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <>
                  {t("clauses:i-agree", "I agree")}
                  <ArrowRight size={15} />
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClausesAcknowledgmentDialog;
